import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as yup from "yup";

import type { OutOfAreaPolicy } from "@thalamos/aspire-common";

export type IcbFormValues = {
  name: string;
  address: string;
  postalCode: string;
  odsCode: string;
  outOfAreaPolicy: OutOfAreaPolicy;
};

export interface IcbFormProps {
  mode: "create" | "edit";
  initialValues: IcbFormValues;
  onSubmit: (values: IcbFormValues) => Promise<void>;
  onCancel: () => void;
}

const validationSchema = yup.object({
  name: yup.string().trim().required("Name is required"),
  address: yup.string().trim().required("Address is required"),
  postalCode: yup.string().trim().required("Postcode is required"),
  // ODS codes are letters and digits, so a mistyped one is caught on the field
  // rather than after the save is sent.
  odsCode: yup
    .string()
    .trim()
    .required("ODS code is required")
    .matches(/^[a-z0-9]+$/i, "ODS code must be letters and numbers only"),
  outOfAreaPolicy: yup
    .string()
    .oneOf(["responsible_icb_pays", "organising_icb_pays"])
    .required(),
});

const POLICY_OPTIONS: Array<{
  value: OutOfAreaPolicy;
  label: string;
  description: string;
}> = [
  {
    value: "responsible_icb_pays",
    label: "Patient's area pays (default)",
    description:
      "An out-of-area assessment is billed to the patient's own local authority, at its rates.",
  },
  {
    value: "organising_icb_pays",
    label: "Organising area pays",
    description:
      "An out-of-area assessment organised by this ICB's AMHP teams is billed to the team's own local authority, at its rates.",
  },
];

export function IcbForm({
  mode,
  initialValues,
  onSubmit,
  onCancel,
}: IcbFormProps) {
  return (
    <Formik<IcbFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) =>
        onSubmit({
          name: values.name.trim(),
          address: values.address.trim(),
          postalCode: values.postalCode.trim(),
          odsCode: values.odsCode.trim().toUpperCase(),
          outOfAreaPolicy: values.outOfAreaPolicy,
        })
      }
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
      }) => (
        <Form noValidate>
          <Box display="flex" flexDirection="column" gap={2}>
            {(
              [
                ["name", "Name"],
                ["address", "Address"],
                ["postalCode", "Postcode"],
                ["odsCode", "ODS code"],
              ] as const
            ).map(([field, label]) => (
              <TextField
                key={field}
                id={`icb-${field}`}
                name={field}
                label={label}
                value={values[field]}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched[field] === true && Boolean(errors[field])}
                helperText={touched[field] === true ? errors[field] : undefined}
                fullWidth
              />
            ))}

            <FormControl component="fieldset">
              <FormLabel component="legend">
                Out-of-area payment policy
              </FormLabel>
              <RadioGroup
                name="outOfAreaPolicy"
                value={values.outOfAreaPolicy}
                onChange={handleChange}
              >
                {POLICY_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography>{option.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {mode === "create" ? "Create" : "Save"}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
}
